import React, { MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import { ArtData } from '@/database/art'
import DetailsPanel from '@/components/mint/details/DetailsPanel'
import TokenomicsPanel from '@/components/mint/tokenomics/TokenomicsPanel'
import VotesClaimsPanel from '@/components/mint/votes/VotesClaimsPanel'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import { transferFeeText } from '@/utils/formatting'
import Box from '@/components/boxes/Box'
import ChatPanel from '@/components/panels/ChatPanel'
import VotesTooltip from '@/components/tooltips/VotesTooltip'
import LoadImageButtonOnDemand from '@/components/LoadImageButtonOnDemand'
import EmptyPanel from '@/components/details/empty/EmptyPanel'

const handleClickOutside = (event: { target: any }, modalRef: HTMLDivElement | null, onClose: () => void) => {
    if (!modalRef?.contains(event.target)) {
        //        onClose();
    }
}

const tabName = '#mint'

const calculateSlidesPerView = () => {
    const screenWidth = window.innerWidth
    if (screenWidth >= 714) return 5
    // Adjust the scaling logic below as needed
    return Math.max(1, Math.floor(screenWidth / 108)) // Example scaling logic
}

const ModelMintCardDetails = ({ artData, onClose }: { artData: ArtData; onClose: () => void }): ReactElement => {
    const modalRef: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const [slidesPerView, setSlidesPerView] = useState(6)

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
        const refElement = modalRef.current
        document.addEventListener('mousedown', (event) => handleClickOutside(event, refElement, onClose))
        const handleResize = () => {
            const slidesPerViewNeeded = calculateSlidesPerView()
            if (slidesPerViewNeeded !== slidesPerView) {
                setSlidesPerView(slidesPerViewNeeded)
            }
        }
        handleResize()
        window.addEventListener('resize', handleResize, false)

        return () => {
            document.removeEventListener('mousedown', (event) => handleClickOutside(event, refElement, onClose))
            window.removeEventListener('resize', handleResize, false)
        }
    }, [])

    return (
        <div ref={modalRef}>
            {artData && (
                <div className="header-container center">
                    <div key={'mint-details' + artData.art.id} className={'artwork-box'}>
                        <div className={'header-container'}>
                            <Box caption={'Title'} text={artData.art.title} />
                        </div>
                        <div className={'header-container bottom'}>
                            <Box caption={'Description'} text={artData.art.prompt} />
                        </div>
                        <LoadImageButtonOnDemand artData={artData} />
                        <div className="header-container bottom">
                            <Box
                                text={transferFeeText(artData)}
                                textValue={artData?.art.transferFee}
                                caption={'Transfer Fee'}
                            />
                            <div id={'votes' + artData.art.id} className={'p-copy'}>
                                <Box
                                    text={artData.claimPower.toFixed(2)}
                                    caption={'Votes'}
                                    styleCaption={'gradient-text bold'}
                                />
                            </div>
                        </div>
                        <VotesTooltip artData={artData} />
                    </div>
                </div>
            )}
            <Tabs defaultFocus={false} selectedIndex={activeIndex} onSelect={() => {}}>
                <Swiper
                    slidesPerView={slidesPerView}
                    spaceBetween={0}
                    freeMode={true}
                    onClick={(index) => {
                        setActiveIndex(index.clickedIndex)
                    }}
                    modules={[FreeMode, Navigation]}
                    navigation={slidesPerView < 5}
                >
                    <TabList>
                        <SwiperSlide className="no-border">
                            <Tab>Your Comments</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Details</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Tokenomics</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Votes</Tab>
                        </SwiperSlide>
                        <SwiperSlide className="no-border">
                            <Tab>Activity</Tab>
                        </SwiperSlide>
                    </TabList>
                </Swiper>
                <div>
                    <TabPanel>
                        <ChatPanel artData={artData} tabName={tabName} />
                    </TabPanel>
                    <TabPanel>
                        <DetailsPanel artData={artData} tabName={tabName} />
                    </TabPanel>
                    <TabPanel>
                        <TokenomicsPanel artData={artData} tabName={tabName} />
                    </TabPanel>
                    <TabPanel>
                        <VotesClaimsPanel artData={artData} tabName={tabName} />
                    </TabPanel>
                    <TabPanel>
                        <EmptyPanel artData={artData} />
                    </TabPanel>
                </div>
            </Tabs>
        </div>
    )
}

export default ModelMintCardDetails
